import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, type GameSubmissionWithGame, type PitchCount } from '../services/api';

@customElement('game-review-queue')
export class GameReviewQueue extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private pendingGames: GameSubmissionWithGame[] = [];

  @state()
  private loading = true;

  @state()
  private selectedGame: GameSubmissionWithGame | null = null;

  @state()
  private changeMessage = '';

  @state()
  private processing = false;

  @state()
  private error = '';

  @state()
  private toast: { type: 'success' | 'error'; message: string } | null = null;

  @state()
  private pitchCountsByGame: Map<string, PitchCount[]> = new Map();

  async connectedCallback() {
    super.connectedCallback();
    await this.loadPendingReviews();
  }

  private async loadPendingReviews() {
    this.loading = true;
    this.error = '';
    try {
      this.pendingGames = (await api.getPendingReviewGames()) || [];

      const pitchCountResults = await Promise.all(
        this.pendingGames.map(async (game) => {
          try {
            const pitches = (await api.getGamePitches(game.game_id)) || [];
            return { gameId: game.game_id, pitches };
          } catch {
            return { gameId: game.game_id, pitches: [] };
          }
        })
      );

      this.pitchCountsByGame = new Map(
        pitchCountResults.map((r) => [r.gameId, r.pitches])
      );
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load';
    } finally {
      this.loading = false;
    }
  }

  private async approve(game: GameSubmissionWithGame) {
    this.processing = true;
    try {
      await api.coachApproveGame(game.game_id, game.id);
      this.pendingGames = this.pendingGames.filter((g) => g.id !== game.id);
      this.showToast('success', 'Game approved');
    } catch (err) {
      this.showToast('error', err instanceof Error ? err.message : 'Approval failed');
    } finally {
      this.processing = false;
    }
  }

  private async requestChanges() {
    if (!this.selectedGame || !this.changeMessage.trim()) return;

    this.processing = true;
    try {
      await api.coachRequestChanges(this.selectedGame.game_id, this.selectedGame.id, this.changeMessage);
      this.pendingGames = this.pendingGames.filter((g) => g.id !== this.selectedGame?.id);
      this.selectedGame = null;
      this.changeMessage = '';
      this.showToast('success', 'Changes requested');
    } catch (err) {
      this.showToast('error', err instanceof Error ? err.message : 'Request failed');
    } finally {
      this.processing = false;
    }
  }

  private showToast(type: 'success' | 'error', message: string) {
    this.toast = { type, message };
    setTimeout(() => { this.toast = null; }, 3000);
  }

  private closeModal() {
    this.selectedGame = null;
    this.changeMessage = '';
  }

  render() {
    if (this.loading) {
      return html`
        <div class="space-y-4">
          ${[1, 2].map(() => html`<div class="skeleton h-24 w-full"></div>`)}
        </div>
      `;
    }

    if (this.error) {
      return html`
        <div class="alert alert-error">
          <span>${this.error}</span>
          <button class="btn btn-sm" @click=${this.loadPendingReviews}>Retry</button>
        </div>
      `;
    }

    if (this.pendingGames.length === 0) {
      return html`${this.renderToast()}`;
    }

    return html`
      <div class="space-y-4">
        <div class="card bg-warning/10 border border-warning/20">
          <div class="card-body p-4">
            <div class="flex items-center gap-2">
              <span class="badge badge-warning">${this.pendingGames.length}</span>
              <span class="font-medium">Game${this.pendingGames.length !== 1 ? 's' : ''} awaiting your review</span>
            </div>
          </div>
        </div>

        ${this.pendingGames.map((game) => this.renderPendingGame(game))}
        ${this.selectedGame ? this.renderRequestChangesModal() : nothing}
        ${this.renderToast()}
      </div>
    `;
  }

  private renderPendingGame(game: GameSubmissionWithGame) {
    const pitchCounts = this.pitchCountsByGame.get(game.game_id) || [];
    const totalPitches = pitchCounts.reduce((sum, pc) => sum + pc.count, 0);

    return html`
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body p-4">
          <div class="flex flex-col gap-3">
            <div>
              <h4 class="font-medium">${game.game_name}</h4>
              <div class="text-sm text-base-content/70">
                ${new Date(game.game_date).toLocaleDateString()}
                ${game.location ? html` - ${game.location}` : nothing}
              </div>
              <div class="text-xs text-base-content/50 mt-1">
                Submitted by ${game.submitted_by_name || 'Unknown'}
              </div>
            </div>

            <!-- Pitch counts -->
            ${pitchCounts.length > 0 ? html`
              <div class="border-t border-base-200 pt-2">
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-sm font-medium">Pitch Counts</span>
                  <span class="badge badge-sm badge-primary">${totalPitches} total</span>
                </div>
                <div class="flex flex-wrap gap-2">
                  ${pitchCounts.map((pc) => html`
                    <div class="badge badge-outline gap-1">
                      ${pc.jersey_number != null ? html`<span class="text-xs text-base-content/50">#${pc.jersey_number}</span>` : nothing}
                      <span>${pc.player_name}</span>
                      <span class="font-semibold">${pc.count}</span>
                    </div>
                  `)}
                </div>
              </div>
            ` : nothing}

            <!-- Actions -->
            <div class="flex gap-2">
              <button
                class="btn btn-success btn-sm flex-1"
                ?disabled=${this.processing}
                @click=${() => this.approve(game)}
              >
                ${this.processing
                  ? html`<span class="loading loading-spinner loading-xs"></span>`
                  : html`<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>`}
                Approve
              </button>
              <button
                class="btn btn-warning btn-outline btn-sm flex-1"
                ?disabled=${this.processing}
                @click=${() => { this.selectedGame = game; }}
              >
                Request Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderRequestChangesModal() {
    return html`
      <div class="modal modal-open">
        <div class="modal-box w-11/12 max-w-lg">
          <h3 class="font-bold text-lg">Request Changes</h3>
          <p class="py-2 text-base-content/70">
            Game: <span class="font-medium">${this.selectedGame?.game_name}</span>
          </p>

          <div class="form-control">
            <label class="label">
              <span class="label-text">What needs to be changed? *</span>
            </label>
            <textarea
              class="textarea textarea-bordered h-24"
              placeholder="Describe what pitch counts need correcting..."
              .value=${this.changeMessage}
              @input=${(e: Event) => { this.changeMessage = (e.target as HTMLTextAreaElement).value; }}
            ></textarea>
          </div>

          <div class="modal-action flex-col-reverse sm:flex-row gap-2">
            <button class="btn btn-ghost w-full sm:w-auto" @click=${this.closeModal} ?disabled=${this.processing}>
              Cancel
            </button>
            <button
              class="btn btn-warning w-full sm:w-auto"
              @click=${this.requestChanges}
              ?disabled=${this.processing || !this.changeMessage.trim()}
            >
              ${this.processing ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing}
              Request Changes
            </button>
          </div>
        </div>
        <div class="modal-backdrop" @click=${this.closeModal}></div>
      </div>
    `;
  }

  private renderToast() {
    if (!this.toast) return nothing;
    return html`
      <div class="toast toast-end">
        <div class="alert ${this.toast.type === 'success' ? 'alert-success' : 'alert-error'}">
          <span>${this.toast.message}</span>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'game-review-queue': GameReviewQueue;
  }
}
